import { useEffect, useRef } from "react";
import { X } from "lucide-react";

const MODE_LABELS = {
  today: "Today",
  future_need: "Future Need",
  tx_plan: "Tx Plan",
};

export default function MobileProcedurePaletteSheet({
  open,
  onClose,
  mode = "today",
  children,
}) {
  const closeButtonRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    closeButtonRef.current?.focus();
    return () => returnFocusRef.current?.focus?.();
  }, [open]);

  if (!open) return null;

  const modeLabel = MODE_LABELS[mode] || "Chairside";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center min-[900px]:hidden"
      data-testid="mobile-procedure-palette-sheet"
    >
      <button
        type="button"
        tabIndex={-1}
        className="absolute inset-0 bg-slate-900/45"
        aria-label="Close procedure picker"
        onClick={onClose}
      />

      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-procedure-palette-title"
        className="relative flex max-h-[88dvh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl motion-safe:animate-[slide-up_0.18s_ease-out]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-center pb-1 pt-2" aria-hidden="true">
          <div className="h-1 w-12 rounded-full bg-slate-300" />
        </div>

        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-2">
          <div className="min-w-0">
            <h2 id="mobile-procedure-palette-title" className="text-base font-semibold text-slate-900">
              Choose procedure
            </h2>
            <p className="text-xs text-slate-500">{modeLabel}: choose one, then tap the tooth.</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            data-testid="mobile-procedure-palette-close"
            aria-label="Close procedure picker"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3">
          {children}
        </div>
      </section>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
